<script setup lang="ts">
// Image component with automatic aspect ratio selection and fallback image
import { toRef } from "vue";
import { type ImageDto, type Uuid } from "luminary-shared";
import LImageProvider, {
    aspectRatiosCSS,
    sizes,
    type AspectRatio,
    type ImageSize,
} from "./LImageProvider.vue";
import { useBucketInfo } from "@/composables/useBucketInfo";

type Props = {
    image?: ImageDto;
    contentParentId?: Uuid;
    parentImageBucketId?: Uuid;
    aspectRatio?: AspectRatio;
    size?: ImageSize;
    rounded?: boolean;
    isModal?: boolean;
    /** Direct image URL for simple rendering (e.g. icons). Bypasses responsive image logic. */
    src?: string;
    /** Opacity applied to the image (0–1). Only used with the `src` prop. */
    opacity?: number;
};
const props = withDefaults(defineProps<Props>(), {
    size: "post",
    rounded: true,
    isModal: false,
    opacity: 1,
});

// Get bucket information for constructing image URLs
const bucketIdRef = toRef(props, "parentImageBucketId");
const { bucketBaseUrl } = useBucketInfo(bucketIdRef);

const rounding = {
    smallSquare: "rounded-md",
    small: "rounded-md",
    thumbnail: "rounded-lg",
    thumbnailFeatured: "rounded-xl",
    thumbnailCompact: "rounded-lg",
    post: "md:rounded-lg",
    card: "rounded-lg",
    icon: "rounded-none",
};
</script>

<template>
    <!-- Simple src mode: direct URL rendering (e.g. for icons) -->
    <img
        v-if="src"
        :src="src"
        alt=""
        class="h-full w-full object-contain"
        :style="opacity !== 1 ? { opacity } : undefined"
    />
    <!-- Icon mode: simple contained rendering, no aspect ratio or cover cropping -->
    <div
        v-else-if="size === 'icon'"
        class="h-full w-full"
    >
        <LImageProvider
            :parent-id="contentParentId!"
            :image="props.image"
            :rounded="false"
            :size="props.size"
            :is-icon="true"
            :bucketPublicUrl="bucketBaseUrl"
            :key="props.image ? JSON.stringify(props.image) : 'empty'"
        />
    </div>
    <div
        v-else
        :class="isModal ? '' : sizes[size]"
    >
        <div
            v-if="!isModal"
            :class="[
                aspectRatio ? aspectRatiosCSS[aspectRatio] : '',
                rounded ? rounding[size] : '',
                'relative w-full overflow-clip bg-cover bg-center object-cover shadow',
            ]"
        >
            <LImageProvider
                :parent-id="contentParentId!"
                :image="props.image"
                :aspect-ratio="props.aspectRatio"
                :rounded="props.rounded"
                :size="props.size"
                :is-modal="props.isModal"
                :bucketPublicUrl="bucketBaseUrl"
                :key="props.image ? JSON.stringify(props.image) : 'empty'"
            />
            <div class="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
                <slot name="imageOverlay"></slot>
            </div>
        </div>

        <!-- Modal mode: no container constraints -->
        <LImageProvider
            v-else
            :parent-id="contentParentId!"
            :image="props.image"
            :rounded="props.rounded"
            :size="props.size"
            :is-modal="props.isModal"
            :bucketPublicUrl="bucketBaseUrl"
            :key="props.image ? JSON.stringify(props.image) : 'empty'"
        />

        <slot></slot>
    </div>
</template>
